<template lang="pug">

v-container(fluid)
    v-card(class="optimization-parameters-card pa-0 mb-4 mt-1" light)
        .optimization-header.pa-3.d-flex.align-center
            v-icon.mr-2(color="primary" size="28") tune
            span.text-h6.font-weight-bold Optimization Overview
        v-divider
        v-list(dense class="pa-2")
            v-list-item
                v-list-item-icon
                    v-icon(:color="optimizationStats?.foundFeasible ? 'success' : 'error'") check_circle
                v-list-item-content
                    v-list-item-title Found feasible solution
            v-list-item
                v-list-item-content
                    v-list-item-title Total Attempts: {{ optimizationStats?.totalAttempts }}
            v-list-item
                v-list-item-content
                    v-simple-table(class="params-table")
                        thead
                            tr
                                th Parameter
                                th Config
                                th Used
                                th Info
                        tbody
                            tr
                                th Production Max
                                    sup {{optimizationOriginalParameters.prodUom}}
                                td {{formatIntegerWithSeparators(optimizationOriginalParameters.productionMax) }}
                                td {{formatIntegerWithSeparators(optimizationParameters.productionMax) }}
                                td
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") Atts: {{optimizationStats.productionMinAttempt }}
                                        div(class="custom-tooltip")
                                            span Number of attempts to minimize production below the configured maximum.
                                                |  A higher number indicates more effort was needed to find a solution.

                            tr
                                th Storage Max
                                    sup {{optimizationOriginalParameters.storageUom}}
                                td {{formatIntegerWithSeparators(optimizationOriginalParameters.storageMax) }}
                                td {{formatIntegerWithSeparators(optimizationParameters.storageMax) }}
                                td
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") HB: {{ optimizationStats.hitBounds.storageMaxLower }}
                                        div(class="custom-tooltip")
                                            span When true, storage is capped at the configured maximum.
                                                |  The optimizer did not reduce below this cap.
                                                |  To allow a lower value, adjust configuration.
                            tr
                                th Storage Min
                                    sup {{optimizationOriginalParameters.storageUom}}
                                td {{formatIntegerWithSeparators(optimizationOriginalParameters.storageMin) }}
                                td {{formatIntegerWithSeparators(optimizationParameters.storageMin) }}
                                td
                                    v-tooltip(bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            span(v-bind="attrs" v-on="on" class="tooltip-activator") HB: {{ optimizationStats.hitBounds.storageMinLower }}
                                        div(class="custom-tooltip")
                                            span When true: storage reserve held at the configured minimum.
                                                |  It cannot go lower without a configuration change.
                                    v-tooltip(v-if="hasStorageMinSuggestion" bottom :max-width="320" :color="'grey darken-4'" transition="scale-transition")
                                        template(v-slot:activator="{ on, attrs }")
                                            v-icon(v-bind="attrs" v-on="on" color="warning" small class="ml-1") lightbulb
                                        div(class="custom-tooltip")
                                            span Lowering storage minimum to {{ formatIntegerWithSeparators(optimizationStats.suggestedStorageMin) }} {{ optimizationOriginalParameters.storageUom }} could save
                                                |  {{ formatVolumeWithUnit(optimizationStats.storageMinSavings, storageUnitShort) }} of production volume over the optimization horizon (5 days).
            v-list-item(v-if="hasStorageMinSuggestion" class="px-2 pt-1")
                v-alert(type="info" dense text border="left" class="mb-0 w-100" color="orange darken-2")
                    span.font-weight-bold Optimization Tip:&nbsp;
                    span Lowering Storage Min from
                        |  {{ formatIntegerWithSeparators(optimizationOriginalParameters.storageMin) }} to {{ formatIntegerWithSeparators(optimizationStats.suggestedStorageMin) }} {{ optimizationOriginalParameters.storageUom }}
                        |  could save {{ formatVolumeWithUnit(optimizationStats.storageMinSavings, storageUnitShort) }} of production volume over the optimization horizon (5 days).
            v-list-item(v-if="hasDailyStorageMetrics")
                v-list-item-content
                    v-list-item-title Day 1 Storage Behavior
                    v-simple-table(class="params-table mt-1")
                        tbody
                            tr
                                th Peak Storage
                                td {{ formatIntegerWithSeparators(dailyStorageMetrics.peakStorage) }} {{ storageUnitShort }}
                            tr
                                th Lowest Storage
                                td {{ formatIntegerWithSeparators(dailyStorageMetrics.lowestStorage) }} {{ storageUnitShort }}
                            tr
                                th Hours At Storage Max
                                td {{ dailyStorageMetrics.hoursAtMax }}
                            tr
                                th Hours At Storage Min
                                td {{ dailyStorageMetrics.hoursAtMin }}
                            tr
                                th End-of-Day Storage Change
                                td(:class="dailyStorageMetrics.endOfDayDelta < 0 ? 'metric-negative' : 'metric-positive'") {{ formatSignedIntegerWithUnit(dailyStorageMetrics.endOfDayDelta, storageUnitShort) }}
</template>

<style lang="css" scoped>

    .optimization-parameters-card {
        background: linear-gradient(135deg, #e3f2fd 0%, #f5f5f5 100%);
        border: 1px solid #1976D2;
        color: #115293;
        border-radius: 14px;
        box-shadow: 0 4px 16px rgba(25, 118, 210, 0.10);
        overflow: hidden;
        max-height: 420px; /* Adjust as needed to match FlowChart height */
        display: flex;
        flex-direction: column;
    }
    .optimization-header {
        background-color: #00355c;
        color: #fff;
        border-top-left-radius: 14px;
        border-top-right-radius: 14px;
        font-size: 1.15em;
        letter-spacing: 0.5px;
    }
    .optimization-parameters-card .v-list {
        overflow-y: auto;
        flex: 1 1 auto;
        max-height: 320px; /* Ensures list scrolls if content overflows */
    }
    .optimization-parameters-card .v-list-item {
        padding: 2px 0 2px 8px;
        border-radius: 6px;
        margin-bottom: 0;
    }
    .optimization-parameters-card .v-list-item-title {
        font-weight: 600;
        color: #00355c;
        font-size: 1em;
    }
    .optimization-parameters-card .v-list-item-subtitle {
        color: #333;
        font-size: 0.95em;
    }
    .optimization-parameters-card .v-list-item-icon {
        margin-right: 10px;
    }

    /* Ensure v-simple-table header is bold (override Vuetify) */
    .params-table >>> .v-data-table__wrapper > table > thead > tr > th {
        font-weight: 800 !important;
        color: #00355C !important;
    }
    /* Ensure v-simple-table header is bold (override Vuetify) */
    .params-table >>> .v-data-table__wrapper > table > tbody > tr > th {
        font-weight: 800 !important;
        color: #0a293f !important;
    }
    .custom-tooltip {
        padding: 12px;
        color: #fff;
        background: #222;
        border-radius: 8px;
        max-width: 320px;
        white-space: normal;
        position: relative;
        font-size: 1em;
        line-height: 1.4;
        box-shadow: 0 2px 8px rgba(0,0,0,0.18);
    }
    .tooltip-activator {
        cursor: help;
        text-decoration: underline;
        color: #1976D2;
    }
    .metric-negative {
        color: #b71c1c;
        font-weight: 700;
    }
    .metric-positive {
        color: #1b5e20;
        font-weight: 700;
    }
</style>

<script lang="js">

export default {
    name: 'OptimizationInfo',
    props: {
        optimizationOriginalParameters: {
            type: Object,
            required: true
        },
        optimizationParameters: {
            type: Object,
            required: true
        },
        optimizationStats: {
            type: Object,
            required: true
        },
        latestSeriesData: {
            type: Array,
            default: () => []
        }
    },
    data () {
        return {
        };
    },
    computed: {
        hasStorageMinSuggestion () {
            return Number.isFinite(this.optimizationStats?.suggestedStorageMin)
                && Number(this.optimizationStats?.storageMinSavings) > 0;
        },
        storageUnitShort () {
            const storageUnit = this.optimizationOriginalParameters?.storageUom;
            if (storageUnit === 'Gal') {
                return 'G';
            }

            return storageUnit || '';
        },
        storageSeries () {
            const storageSeries = (this.latestSeriesData || []).find((series) => series?.name === 'storage');
            return Array.isArray(storageSeries?.data)
                ? storageSeries.data.filter((value) => Number.isFinite(Number(value))).map(Number)
                : [];
        },
        hasDailyStorageMetrics () {
            return this.storageSeries.length > 0;
        },
        dailyStorageMetrics () {
            if (!this.hasDailyStorageMetrics) {
                return null;
            }

            const usedStorageMax = Number(this.optimizationParameters?.storageMax);
            const usedStorageMin = Number(this.optimizationParameters?.storageMin);
            const peakStorage = Math.max(...this.storageSeries);
            const lowestStorage = Math.min(...this.storageSeries);
            const firstStorage = this.storageSeries[0];
            const lastStorage = this.storageSeries[this.storageSeries.length - 1];
            const endOfDayDelta = lastStorage - firstStorage;

            return {
                peakStorage,
                lowestStorage,
                hoursAtMax: Number.isFinite(usedStorageMax)
                    ? this.storageSeries.filter((value) => this.isNearBound(value, usedStorageMax)).length
                    : 0,
                hoursAtMin: Number.isFinite(usedStorageMin)
                    ? this.storageSeries.filter((value) => this.isNearBound(value, usedStorageMin)).length
                    : 0,
                endOfDayDelta,
            };
        }
    },
    methods: {
        formatNumber (value) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return '--';
            }
            return Number.parseFloat(numericValue.toFixed(3));
        },
        formatIntegerWithSeparators (value) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return '--';
            }

            return Math.round(numericValue).toLocaleString('en-US');
        },
        formatVolumeWithUnit (value, unit) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return `0 ${unit || ''}`.trim();
            }

            const clamped = Math.max(0, numericValue);
            const formatted = clamped >= 100
                ? Math.round(clamped).toLocaleString('en-US')
                : Number.parseFloat(clamped.toFixed(3)).toLocaleString('en-US');
            return `${formatted} ${unit || ''}`.trim();
        },
        formatSignedIntegerWithUnit (value, unit) {
            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return `0 ${unit}`;
            }

            const roundedValue = Math.round(numericValue);
            const sign = roundedValue > 0 ? '+' : '';
            return `${sign}${roundedValue.toLocaleString('en-US')} ${unit}`;
        },
        isNearBound (value, bound) {
            const tolerance = Math.max(0.001, Math.abs(bound) * 0.000001);
            return Math.abs(Number(value) - Number(bound)) <= tolerance;
        }
    }
};


</script>
